/** The engine's data (docs/spec/02, 06 §4). */

import type { Rng } from './rng';

/** Input directions. `DIR_*` below; 0 is no direction. */
export type Dir = 0 | 1 | 2 | 3 | 4;
export const DIR_NONE: Dir = 0;
export const DIR_UP: Dir = 1;
export const DIR_RIGHT: Dir = 2;
export const DIR_DOWN: Dir = 3;
export const DIR_LEFT: Dir = 4;
export const DX: readonly number[] = [0, 0, 1, 0, -1];
export const DY: readonly number[] = [0, -1, 0, 1, 0];

/** What the player does this scan. */
export interface Input {
  readonly dir: Dir;
  /** Held: grab the cell in `dir` without moving. */
  readonly fire: boolean;
}

export const NO_INPUT: Input = { dir: DIR_NONE, fire: false };

/** A cave's numbers for one level, fixed for the run. */
export interface CaveParams {
  readonly tickMs: number;
  readonly diamondsNeeded: number;
  readonly diamondValue: number;
  readonly extraValue: number;
  /** Seconds. */
  readonly time: number;
  /** Seconds of slow amoeba growth after Digby's birth. */
  readonly amoebaTime: number;
  /** Seconds a magic wall runs once started. */
  readonly magicWallTime: number;
  /** 1-5: the time bonus per second left. */
  readonly level: number;
  readonly intermission: boolean;
}

/** A built cave, ready to run: what `src/caves` hands the engine. */
export interface CaveSetup {
  readonly w: number;
  readonly h: number;
  readonly cells: Uint8Array;
  readonly params: CaveParams;
}

export type CaveStatus = 'playing' | 'won' | 'dead' | 'timeout';
export type AmoebaPhase = 'sleeping' | 'awake' | 'enclosed' | 'tooBig';
export type MagicPhase = 'dormant' | 'active' | 'expired';

export interface DigbyState {
  x: number;
  y: number;
  born: boolean;
  alive: boolean;
  /** -1 left, 1 right: the last horizontal direction. */
  facing: -1 | 1;
  /** Moved this scan. */
  moving: boolean;
  /** Pushed against a boulder this scan (whether or not it went). */
  pushing: boolean;
  /** Scans since he last moved or grabbed: drives the idle animation. */
  idle: number;
}

export interface AmoebaState {
  phase: AmoebaPhase;
  /** Cells counted in the last complete scan. */
  size: number;
  /** Growing this scan (for the bubbling sound). */
  growing: boolean;
  count: number;
  canGrow: boolean;
}

export interface MagicState {
  phase: MagicPhase;
  msLeft: number;
}

export type SimEvent =
  | { readonly type: 'dig'; readonly x: number; readonly y: number }
  | { readonly type: 'collect'; readonly x: number; readonly y: number; readonly what: 'diamond' | 'key' | 'clock' }
  | { readonly type: 'land'; readonly x: number; readonly y: number; readonly what: 'boulder' | 'diamond' }
  | { readonly type: 'push'; readonly x: number; readonly y: number }
  | { readonly type: 'explode'; readonly x: number; readonly y: number; readonly kind: 'space' | 'diamonds' }
  | { readonly type: 'magicConvert'; readonly x: number; readonly y: number }
  | { readonly type: 'born'; readonly x: number; readonly y: number }
  | { readonly type: 'died'; readonly x: number; readonly y: number }
  | { readonly type: 'gateOpen' }
  | { readonly type: 'won'; readonly x: number; readonly y: number }
  | { readonly type: 'tick'; readonly seconds: number }
  | { readonly type: 'timeout' }
  | { readonly type: 'doorOpen'; readonly color: number }
  | { readonly type: 'expand'; readonly x: number; readonly y: number }
  | { readonly type: 'slime'; readonly x: number; readonly y: number };

export type SimEventType = SimEvent['type'];

/** Everything about a running cave. Presentation reads it and never writes it. */
export interface CaveState {
  readonly w: number;
  readonly h: number;
  readonly cells: Uint8Array;
  /** The scan number that last wrote each cell (02 §1). */
  readonly stamp: Uint32Array;
  /** The direction (`Dir`) the object now in each cell arrived from this scan; 0 if none. */
  readonly moved: Uint8Array;
  readonly params: CaveParams;
  readonly rng: Rng;
  readonly seed: number;
  /** Scans completed. */
  scan: number;
  /** Cave time since the start, ms. */
  ms: number;
  /** Cave time of Digby's birth, or -1. */
  bornMs: number;
  hatchScan: number;
  timeLeft: number;
  /** Cave time toward the next second of `timeLeft`. */
  secondMs: number;
  diamonds: number;
  /** Points earned in this cave so far (diamonds; the time bonus is the session's). */
  score: number;
  gateOpen: boolean;
  status: CaveStatus;
  readonly digby: DigbyState;
  readonly amoeba: AmoebaState;
  readonly magic: MagicState;
  readonly keys: boolean[];
  /** This scan's events, replaced every step. */
  readonly events: SimEvent[];
}
